import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const CARD_PADDING = designTokens.spacing[6];
const GRID_COL_GAP = designTokens.spacing[10];
const GRID_ROW_GAP = designTokens.spacing[5];
const HEADER_FONT_SIZE = designTokens.fontSize.sm;
const LABEL_FONT_SIZE = designTokens.fontSize.xxs;
const LABEL_LETTER_SPACING = designTokens.letterSpacing.wide;

const SKELETON_CARD_TITLE_WIDTH = "140px";
const SKELETON_LABEL_WIDTH = "60px";
const SKELETON_LABEL_MARGIN_BOTTOM = "6px";
const SKELETON_VALUE_WIDTH = "120px";
const SKELETON_DESC_LABEL_WIDTH = "80px";
const SKELETON_TEMPLATE_TITLE_WIDTH = "220px";
const SKELETON_TEMPLATE_BTN_PRIMARY_WIDTH = "110px";
const SKELETON_TEMPLATE_BTN_SECONDARY_WIDTH = "80px";

export const useDiagnosticOverviewTabStyles = makeStyles({
  root: {
    paddingBottom: designTokens.spacing[6],
  },

  card: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: CARD_PADDING,
    fontFamily: projectIqTokens.fontFamily,
  },

  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: designTokens.spacing[3],
  },

  cardHeader: {
    textTransform: "uppercase",
    fontSize: HEADER_FONT_SIZE,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: "0",
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "0",
    paddingRight: "0",
  },

  divider: {
    borderTop: `1px solid ${projectIqTokens.line}`,
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "none",
    marginTop: designTokens.spacing[4],
    marginBottom: designTokens.spacing[4],
    marginLeft: "0",
    marginRight: "0",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: GRID_COL_GAP,
    rowGap: GRID_ROW_GAP,
    marginTop: "0",
    marginBottom: "0",
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },

  metaLabel: {
    display: "block",
    textTransform: "uppercase",
    fontSize: LABEL_FONT_SIZE,
    letterSpacing: LABEL_LETTER_SPACING,
    color: projectIqTokens.muted,
    fontWeight: designTokens.fontWeight.semibold,
  },

  metaValue: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.ink,
    fontWeight: designTokens.fontWeight.normal,
    marginTop: designTokens.spacing[1],
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    display: "block",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },

  gridCellFullWidth: {
    gridColumn: "1 / -1",
  },

  metaValueEmpty: {
    fontStyle: "italic",
    color: projectIqTokens.muted,
    fontSize: designTokens.fontSize.sm,
    marginTop: designTokens.spacing[1],
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    display: "block",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },

  templateCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: CARD_PADDING,
    fontFamily: projectIqTokens.fontFamily,
    marginTop: designTokens.spacing[4],
  },

  templateCardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[3],
  },

  templateCardDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: designTokens.accent[500],
  },

  templateCardMeta: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
    color: projectIqTokens.muted,
  },

  templateCardDesc: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.ink,
    lineHeight: "1.6",
    marginTop: "0",
    marginBottom: designTokens.spacing[3],
    marginLeft: "0",
    marginRight: "0",
  },

  templateCardWhenToUse: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    marginTop: "0",
    marginBottom: designTokens.spacing[4],
    marginLeft: "0",
    marginRight: "0",
  },

  templateCardWhenToUseLabel: {
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
  },

  templateCardActions: {
    display: "flex",
    flexDirection: "row",
    gap: designTokens.spacing[2],
    flexWrap: "wrap",
  },

  templateCardLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    textDecoration: "none",
    background: "transparent",
    cursor: "pointer",
  },

  templateCardLinkPrimary: {
    border: `1px solid ${designTokens.accent[500]}`,
    color: designTokens.accent[500],
  },

  templateCardLinkSecondary: {
    border: `1px solid ${projectIqTokens.muted}`,
    color: projectIqTokens.muted,
  },

  skeletonCardTitle: {
    width: SKELETON_CARD_TITLE_WIDTH,
  },

  skeletonLabel: {
    width: SKELETON_LABEL_WIDTH,
    marginBottom: SKELETON_LABEL_MARGIN_BOTTOM,
  },

  skeletonValue: {
    width: SKELETON_VALUE_WIDTH,
  },

  skeletonDescriptionLabel: {
    width: SKELETON_DESC_LABEL_WIDTH,
    marginBottom: SKELETON_LABEL_MARGIN_BOTTOM,
  },

  skeletonDescriptionValue: {
    width: "100%",
  },

  skeletonTemplateTitle: {
    width: SKELETON_TEMPLATE_TITLE_WIDTH,
    marginBottom: designTokens.spacing[3],
  },

  skeletonTemplateDescLine: {
    width: "100%",
    marginBottom: designTokens.spacing[2],
  },

  skeletonTemplateDescLineShort: {
    width: "75%",
    marginBottom: designTokens.spacing[4],
  },

  skeletonTemplateActions: {
    display: "flex",
    gap: designTokens.spacing[2],
  },

  skeletonTemplatePrimaryButton: {
    width: SKELETON_TEMPLATE_BTN_PRIMARY_WIDTH,
    borderRadius: designTokens.borderRadius.md,
  },

  skeletonTemplateSecondaryButton: {
    width: SKELETON_TEMPLATE_BTN_SECONDARY_WIDTH,
    borderRadius: designTokens.borderRadius.md,
  },
});
